import type { AfterViewInit, OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, Component, HostListener, inject, input } from '@angular/core';
import { DropdownTimeoutConfiguration } from '@core/constants/app-constant';
import { SidebarActionsComponent } from '@core/layout/sidebar/components/sidebar-actions/sidebar-actions.component';
import { RedirectService } from '@core/services/redirect.service';
import { RedirectStrategyTypeMap } from '@core/types/redirect.types';
import { NavigationFacade } from '@kerberos-compliance/compliance-shared-ui';
import { NzSiderComponent } from 'ng-zorro-antd/layout';
import { NavigationMenuComponent } from '../navigation-menu/navigation-menu.component';

@Component({
  selector: 'aad-sidebar',
  imports: [NzSiderComponent, NavigationMenuComponent, SidebarActionsComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements AfterViewInit, OnDestroy {
  private readonly redirectService = inject(RedirectService);

  private readonly navigationFacade = inject(NavigationFacade);

  public showLogoOnly = input(false);

  protected readonly isCollapsed = this.navigationFacade.isClosed;

  private mouseLeaveTimeout: ReturnType<typeof setTimeout> | undefined;

  private isDropdownOpen = false;

  private mutationObserver: MutationObserver | undefined;

  private readonly boundHandleGlobalClick: (event: Event) => void;

  public constructor() {
    this.boundHandleGlobalClick = this.handleGlobalClick.bind(this);
  }

  public ngAfterViewInit(): void {
    this.mutationObserver = new MutationObserver(() => {
      const dropdowns = document.querySelectorAll('.ant-dropdown:not(.ant-dropdown-hidden)');
      const wasOpen = this.isDropdownOpen;
      this.isDropdownOpen = dropdowns.length > 0;
      if (wasOpen && !this.isDropdownOpen && !this.isCollapsed()) {
        this.checkSidebarCollapse();
      }
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    document.addEventListener('click', this.boundHandleGlobalClick);
  }

  public ngOnDestroy(): void {
    this.mutationObserver?.disconnect();

    if (this.mouseLeaveTimeout) {
      clearTimeout(this.mouseLeaveTimeout);
      this.mouseLeaveTimeout = undefined;
    }
    document.removeEventListener('click', this.boundHandleGlobalClick);
  }

  /**
   * Handles global click events to determine if dropdown-related elements are clicked
   * and performs necessary actions such as checking sidebar collapse.
   *
   * @param {Event} event - The DOM event triggered by a global click.
   * @returns {void} This method does not return any value.
   */
  private handleGlobalClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target.closest('.ant-dropdown') || target.closest('[nz-dropdown]')) {
      return;
    }
    if (this.isDropdownOpen) {
      setTimeout(() => this.checkSidebarCollapse(), DropdownTimeoutConfiguration.defaultTimeout);
    }
  }

  /**
   * Handles the logic for collapsing the sidebar when certain conditions are met.
   * It checks if the sidebar is not currently collapsed and ensures that the dropdown is not open.
   * If these conditions hold true, the method waits for a predefined timeout and then collapses the sidebar.
   *
   * @returns {void} This method does not return a value.
   */
  private checkSidebarCollapse(): void {
    if (!this.isCollapsed() && !this.isDropdownOpen) {
      setTimeout(() => {
        if (!this.isDropdownOpen && !this.isCollapsed()) {
          this.navigationFacade.close();
        }
      }, DropdownTimeoutConfiguration.defaultTimeout);
    }
  }

  /**
   * Navigates the application to the default route or path as defined by the redirect service.
   *
   * The method uses the `RedirectService` to handle redirection logic
   * based on the `Default` redirect strategy type in the `RedirectStrategyTypeMap`.
   *
   * @returns {void} No return value.
   */
  public navigateToDefault(): void {
    this.redirectService.handleRedirect(RedirectStrategyTypeMap.Default);
  }

  /**
   * Event handler triggered when the mouse pointer enters the host element.
   * It clears any existing mouse leave timeout and ensures the collapsible element is expanded.
   *
   * @returns {void} This method does not return a value.
   */
  @HostListener('mouseenter')
  public onMouseEnter(): void {
    if (this.mouseLeaveTimeout) {
      clearTimeout(this.mouseLeaveTimeout);
      this.mouseLeaveTimeout = undefined;
    }

    if (this.isCollapsed()) {
      this.navigationFacade.open();
    }
  }

  /**
   * Handles the mouse leave event for the host element.
   * Clears any existing timeout and sets a new timeout to trigger the `checkSidebarCollapse` method
   * after a delay specified by `DropdownTimeoutConfiguration.defaultTimeout`.
   *
   * @returns {void} Does not return any value.
   */
  @HostListener('mouseleave')
  public onMouseLeave(): void {
    if (this.mouseLeaveTimeout) {
      clearTimeout(this.mouseLeaveTimeout);
    }

    this.mouseLeaveTimeout = setTimeout(() => {
      this.checkSidebarCollapse();
      this.mouseLeaveTimeout = undefined;
    }, DropdownTimeoutConfiguration.defaultTimeout);
  }
}
